import { Box, FormLabel, Grid, Typography, Tooltip, Paper, Fade, Grow, CircularProgress } from "@mui/material";
import { useSelector } from "react-redux";
import { CommonSelect } from "../common";
import { CommonButton } from "../common/commonButton";
import { useState, useCallback, useEffect, useRef } from "react";
import instance from "../../redux/api";
import { toast } from "react-toastify";
import { read, utils } from "xlsx";
import { policyFileColumnNames } from "./constants";
import { 
  PolicyFileUpload, 
  uploadRbsDetails,
  uploadSiriusData,
  uploadAonLedger,
  uploadMopMapping,
  getUpLoadedBankFiles,
  uploadROEFiles
} from "../../Services/api";
import { useDropzone } from "react-dropzone";
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DescriptionIcon from '@mui/icons-material/Description';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ClearIcon from '@mui/icons-material/Clear';
import FolderIcon from '@mui/icons-material/Folder';
import FloatingAIAssistant from "../AIFloatingAssistant/AIFloatingAssistant";
import { styled, keyframes } from "@mui/material/styles";
import "../common/commonPageStyles.css";

const fileReader = (f, callback) => {
  return new Promise((resolve, reject) => {
    var name = f.name;
    console.log("file name", name);
    const reader = new FileReader();
    reader.onload = (evt) => {
      const data = new Uint8Array(evt.target.result);
      const wb = read(data, { type: "array" });
      const wsname = wb.SheetNames[0];
      const ws = wb.Sheets[wsname];
      const rowObject = utils.sheet_to_json(ws, {
        header: 1,
        defval: "",
      });
      const headers = rowObject[0];
      const missingCols = policyFileColumnNames.filter(col => !headers.includes(col));
      if (missingCols.length > 0) {
        console.log("invalid file, missing columns:", missingCols);
        reject("Invalid file: missing columns " + missingCols.join(", "));
      } else {
        console.log("valid file");
        resolve("Valid file");
      }
    };
    reader.readAsArrayBuffer(f);
  });

  // const data = XLSX.utils.sheet_to_csv(ws, {header:1});
  // /* Update state */
  // console.log("Data>>>"+data);
};

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
    opacity: 0.8;
  }
  50% {
    transform: scale(1.05);
    opacity: 1;
  }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

// Styled Components
const StyledContainer = styled(Box)(({ $toggle }) => ({
  width: "100%",
  maxWidth: "100%",
  minHeight: "525px",
  maxHeight: "calc(100vh - 200px)",
  overflowY: "auto",
  overflowX: "hidden",
  padding: "28px",
  marginTop: "24px",
  marginLeft: "auto",
  marginRight: "auto",
  background: "linear-gradient(135deg, #f0f4f8 0%, #e3eaf2 100%)",
  borderRadius: "20px",
  animation: `${fadeInUp} 0.6s ease-out`,
  display: "flex",
  flexDirection: "column",
  boxSizing: "border-box",
  position: "relative",
  zIndex: 1,
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "radial-gradient(circle at 20% 50%, rgba(22, 114, 222, 0.05) 0%, transparent 50%)",
    pointerEvents: "none",
    borderRadius: "20px",
  },
}));

const TitleContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "12px",
  marginBottom: "24px",
  "& .title-icon": {
    color: "#1672DE",
    fontSize: "32px",
    animation: `${pulse} 2s ease-in-out infinite`,
  },
});

const TitleText = styled(Typography)({
  color: "#1672DE",
  fontSize: "28px",
  fontWeight: "700",
  letterSpacing: "-0.5px",
  background: "linear-gradient(135deg, #1672DE 0%, #1565c0 100%)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  // Using global CSS class for consistency
  "@media (max-width: 768px)": {
    fontSize: "24px",
  },
});

const FormContainer = styled(Paper)({
  padding: "28px",
  borderRadius: "16px",
  boxShadow: "0 6px 24px rgba(22, 114, 222, 0.12)",
  background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
  animation: `${fadeInUp} 0.6s ease-out 0.2s both`,
  flex: "0 0 auto",
  width: "100%",
  maxWidth: "100%",
  overflow: "hidden",
  overflowX: "hidden",
  boxSizing: "border-box",
  border: "1px solid rgba(22, 114, 222, 0.08)",
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "4px",
    background: "linear-gradient(90deg, #1672DE 0%, #1565c0 100%)",
    borderRadius: "16px 16px 0 0",
  },
});

const DropzoneContainer = styled(Box)(({ isDisabled, isDragActive, hasFiles }) => ({
  width: "100%",
  maxWidth: "100%",
  minHeight: hasFiles ? "200px" : "140px",
  border: `2px dashed ${isDragActive ? "#1672DE" : "#b0bec5"}`,
  borderRadius: "12px",
  padding: "20px",
  textAlign: "center",
  backgroundColor: isDragActive ? "rgba(22, 114, 222, 0.05)" : hasFiles ? "rgba(22, 114, 222, 0.03)" : "rgba(22, 114, 222, 0.02)",
  cursor: isDisabled ? "not-allowed" : "pointer",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  position: "relative",
  overflow: "hidden",
  overflowX: "hidden",
  boxSizing: "border-box",
  "&:hover": {
    borderColor: isDisabled ? "#b0bec5" : "#1672DE",
    backgroundColor: isDisabled ? "rgba(22, 114, 222, 0.02)" : "rgba(22, 114, 222, 0.08)",
    transform: isDisabled ? "none" : "translateY(-2px)",
    boxShadow: isDisabled ? "none" : "0 8px 24px rgba(22, 114, 222, 0.15)",
  },
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: "-100%",
    width: "100%",
    height: "100%",
    background: "linear-gradient(90deg, transparent, rgba(22, 114, 222, 0.1), transparent)",
    transition: "left 0.5s",
  },
  "&:hover::before": {
    left: "100%",
  },
}));

const DropzoneContent = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "12px",
  zIndex: 1,
  position: "relative",
  width: "100%",
  maxWidth: "100%",
  boxSizing: "border-box",
  "& *": {
    maxWidth: "100%",
    wordBreak: "break-word",
    overflowWrap: "break-word",
  },
});

const FileListContainer = styled(Box)({
  marginTop: "20px",
  animation: `${fadeInUp} 0.4s ease-out`,
  maxHeight: "200px",
  overflowY: "auto",
  overflowX: "hidden",
  width: "100%",
  maxWidth: "100%",
  boxSizing: "border-box",
  "&::-webkit-scrollbar": {
    width: "6px",
  },
  "&::-webkit-scrollbar-track": {
    background: "#f1f1f1",
    borderRadius: "10px",
  },
  "&::-webkit-scrollbar-thumb": {
    background: "#1672DE",
    borderRadius: "10px",
    "&:hover": {
      background: "#1565c0",
    },
  },
});

const FileItem = styled(Paper)({
  display: "flex",
  alignItems: "center",
  gap: "12px",
  padding: "12px 16px",
  marginBottom: "8px",
  borderRadius: "8px",
  background: "white",
  boxShadow: "0 2px 8px rgba(22, 114, 222, 0.1)",
  transition: "all 0.3s ease",
  animation: `${slideIn} 0.3s ease-out`,
  width: "100%",
  maxWidth: "100%",
  boxSizing: "border-box",
  overflow: "hidden",
  "&:hover": {
    boxShadow: "0 4px 12px rgba(22, 114, 222, 0.2)",
    transform: "translateX(4px)",
  },
});

const ActionButtonsContainer = styled(Box)({
  display: "flex",
  gap: "12px",
  marginTop: "20px",
  animation: `${fadeInUp} 0.6s ease-out 0.4s both`,
});

const InfoTooltip = styled(Box)({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: "20px",
  height: "20px",
  borderRadius: "50%",
  backgroundColor: "#1672DE",
  color: "white",
  fontSize: "12px",
  fontWeight: "bold",
  cursor: "help",
  marginLeft: "8px",
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: "#1565c0",
    transform: "scale(1.1)",
  },
});

const LabelContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "10px",
  marginBottom: "10px",
  "& .label-icon": {
    color: "#1672DE",
    fontSize: "22px",
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "scale(1.1)",
      color: "#1565c0",
    },
  },
});

const StyledFormLabel = styled(FormLabel)({
  color: "#263238",
  fontWeight: "600",
  fontSize: "15px",
  letterSpacing: "0.2px",
  marginBottom: "0",
  "&::after": {
    content: '":"',
    marginLeft: "2px",
  },
});

export const fileList = {
  padding: "0",
  margin: "0",
  listStyle: "none",
};

export const fileItem = {
  display: "flex",
  gap: "10px",
  padding: "5px",
  width: "fit-content",
  margin: "8px 0",
};

export const fileNameStyle = {
  minWidth: "400px",
  color: "#263238",
  fontSize: "14px",
  fontWeight: "500",
};

export const StatementUpLoad = () => {
  //selectors
  const toggle = useSelector((state) => state.toggleSideMenu);
  const { userData } = useSelector((state) => state?.user);

  //Local State
  const [formData, setFormData] = useState({
    name: "",
    file: null,
  });
  const [statementFieldsPrams, setStatementFieldsPrams] = useState({
    statement_type: "",
  });
  const [uploadedFileNames, setUploadedFileNames] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isButtonDisable, setIsButtonDisable] = useState(true);
  const [fileTypeError, setFileTypeError] = useState(false);
  const [isMultiple, setIsMultiple] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef();

  const statementTypes = [

    { id: 1, label: "Bank Statement", api:getUpLoadedBankFiles, regEx:/^bank_statement_(\d{2})-(\d{2})-(\d{4})\.[a-zA-Z0-9]+$/ },
    { id: 2, label: "Remittance", api:PolicyFileUpload, regEx:/^remittance_upload_(\d{2})-(\d{2})-(\d{4})\.[a-zA-Z0-9]+$/ },
    { id: 3, label: "RBS Details", api:uploadRbsDetails, regEx:/^rbs_details_(\d{2})-(\d{2})-(\d{4})\.[a-zA-Z0-9]+$/ },
    { id: 4, label: "Sirius Point", api:uploadSiriusData, regEx:/^sirius_point_(\d{2})-(\d{2})-(\d{4})\.[a-zA-Z0-9]+$/ },
    { id: 5, label: "AON Ledger", api:uploadAonLedger, regEx:/^aon_ledger_(\d{2})-(\d{2})-(\d{4})\.[a-zA-Z0-9]+$/ },
    { id: 6, label: "MOP Mapping", api:uploadMopMapping, regEx:/^mop_mapping_(\d{2})-(\d{2})-(\d{4})\.[a-zA-Z0-9]+$/ },
    { id: 7, label: "ROE", api:uploadROEFiles, regEx:/^roe-(\d{2})-(\d{2})-(\d{4})\.[a-zA-Z0-9]+$/ }

  ];


  const isValidDate = (day, month, year) => {
    // Check if month is between 01 and 12
    if (month < 1 || month > 12) return false;

    // Check if day is valid for the given month and year
    const daysInMonth = new Date(year, month, 0).getDate(); // Get number of days in the month
    if (day < 1 || day > daysInMonth) return false;

    return true;
  };

  const ValidateFileNames = (file) => {

    if (file) {
      const fileName = file.name;
      console.log("statementFieldsPrams?.statement_type", statementFieldsPrams?.statement_type)
      
      const fileNamePattern = statementTypes.find(type => type.label === statementFieldsPrams?.statement_type).regEx;
      
      const match = fileName.match(fileNamePattern);
      
      if (match) {
        const day = parseInt(match[1], 10);
        const month = parseInt(match[2], 10);
        const year = parseInt(match[3], 10);

        // Validate the date
        if (isValidDate(day, month, year)) {
          return {status: true}
        } else {
          return {status: false, message : 'Invalid date in file name. Please ensure the date is valid and in the format dd-mm-yyyy.'}
        }
      } else {
        return {status: false, message: 'Invalid file name format'}
      }
    }
  };

  useEffect(() => {
    if (statementFieldsPrams?.statement_type === "Bank Statement") {
      setIsMultiple(true);
    } else {
      setIsMultiple(false);
    }
  }, [statementFieldsPrams?.statement_type]);

  const handleSubmitFileUpLoad = useCallback(
    async (e) => {
      e.preventDefault();
      setIsButtonDisable(true);
      setIsLoading(true);
      const { file } = formData;
      if (file?.length > 0) {
        const upLoadpromises = file.map((currentFile) => {

          if(!["Remittance", "Bank Statement", "ROE"].includes(statementFieldsPrams?.statement_type)){
            const validate = ValidateFileNames(currentFile)
            if(!validate.status)
              return Promise.reject(validate.message)
          }

          if (statementFieldsPrams?.statement_type === "Remittance") {
            let formData = new FormData();
            formData.append("uploaded_by", userData.user_name);
            formData.append("policy_excel_file", currentFile);
            return instance.post(
              PolicyFileUpload,
              formData,
              {
                headers: {
                  "Content-Type": "multipart/form-data",
                },
              }
            )
            .catch((error) => {
              toast.error(`${error?.data?.message}` ?? "Unknown error occurred during file upload.", {
                position: toast.POSITION.TOP_RIGHT,
                autoClose: 5000,
              });
            });
          }
          else if (statementFieldsPrams?.statement_type === "ROE") {
            let formData = new FormData();
            formData.append("file", currentFile)
            formData.append("created_by", userData?.user_name ?? "")
            return instance.post(
              uploadROEFiles,
              formData,
              {
                headers: {
                  "Content-Type": "multipart/form-data",
                },
              }
            );
          }
          else {
            let formData = new FormData();
            formData.append(
              "csrfmiddlewaretoken",
              "y1ClWtpYmU9QJfLOdlN2SlIcNAzuumg9xsvX9TxbnQvlYv6EKcnKt8X8wrJyntaH"
            );

            if (statementFieldsPrams?.statement_type) {
              formData.append(
                "statement_type",
                statementFieldsPrams.statement_type
              );
            }

            if(statementFieldsPrams?.statement_type === "Bank Statement")
              formData.append("bank_details", currentFile);
            else
              formData.append("file", currentFile);
            
            const apiEndpoint = statementTypes.find(type => type.label === statementFieldsPrams?.statement_type).api;

            return instance.post(
              apiEndpoint,
              formData,
              {
                headers: {
                  "Content-Type": "multipart/form-data",
                },
              }
            );
          }
        });

        const results = await Promise.allSettled(upLoadpromises);

        if (statementFieldsPrams.statement_type !== "Remittance" && statementFieldsPrams.statement_type !== "ROE") {
          let uploadedFileNamesList = [];
          results.forEach((result) => {
            if (result.status === "fulfilled") {
              showToast("success", "File uploaded successfully!")                            
            } else {
              if(typeof result.reason == 'string')
                showToast("error", result.reason)
              else
                showToast("error", result.reason.data.message)
            }
          });

          // Update the state with all uploaded file Names
          setUploadedFileNames([...uploadedFileNamesList]);
        }
        else if (statementFieldsPrams.statement_type === "ROE") {
          if (results[0].status === "fulfilled") {
            const successMessage = results[0].value.data.message || 'File uploaded successfully!';
            showToast("success", successMessage)
          }
          else {
            showToast("error", results[0].reason.data.message)
          }
        }
        else {
          if (results[0].status === "fulfilled") {
            const successMessage = results[0].value.data.results || 'File uploaded successfully!';
            showToast("success", successMessage)
          } else {
            if(typeof results[0].reason == 'string'){
              showToast("error", `${results[0].reason}`)
            }
            else {
              showToast("error", `${results[0].reason.data.message}`)
            }
          }
        }
      } else {
        alert("Please select files to upload.");
      }
      setIsLoading(false);
      setStatementFieldsPrams((old) => ({ ...old, statement_type: "" }));
      setSelectedFiles([]);
      setIsButtonDisable(true);
      inputRef.current.files = [];
    },
    [formData, statementFieldsPrams]
  );

  useEffect(() => {
    if (statementFieldsPrams.statement_type === "ROE") {
      validateROETypeFile();
    }
    else {
      validatePolicyTypeFile();
    }
  }, [selectedFiles, statementFieldsPrams.statement_type]);

  const validatePolicyTypeFile = async () => {
    if (statementFieldsPrams?.statement_type === "Remittance") {
      setIsButtonDisable(true);
      if (selectedFiles.length > 1) {
        showToast("error", "Multiple policy type files not allowed")
        // setFileTypeError(true);
        setSelectedFiles([]);
        inputRef.current.files = [];
        setFormData({
          file: null,
        });
        setIsButtonDisable(true);
        return;
      }

      const result = await Promise.allSettled(
        selectedFiles.map((file) => fileReader(file))
      );
      if (result[0].status !== "fulfilled") {
        showToast("error",result[0].reason ?? "Invalid File")
        setFileTypeError(true);
        setSelectedFiles([]);
        setFormData({
          file: null,
          name: ""
        });
        setIsButtonDisable(false);
      } else {
        setFileTypeError(false);
        setIsButtonDisable(false);
      }
    } else if (statementFieldsPrams?.statement_type === "Bank Statement") {
      if (selectedFiles.length > 0) {
        setIsButtonDisable(false);
        setFileTypeError(false);
      } else {
        setIsButtonDisable(true);
        setFileTypeError(false);
      }
    }
    setIsLoading(false);
  };

  const validateROETypeFile = async () => {
    setIsButtonDisable(true);
    if (selectedFiles.length > 1) {
      showToast("error", "Multiple ROE type files not allowed")
      setSelectedFiles([]);
      setFormData({
        file: null,
        name: ""
      });
      setIsButtonDisable(true);
      return;
    }

    const file_name_regex = /^roe-\d{2}-\d{2}-\d{4}..*$/;
    const file = selectedFiles[0]
    if (!file) return;

    if (!file_name_regex.test(file.name)) {
      showToast("error", "File name is invalid.")
      setSelectedFiles([]);
      setFormData({
        file: null,
        name: ""
      });
      setIsButtonDisable(true);
      setIsLoading(false);
      return;
    }
    else {
      const day = parseInt(file.name.substring(4, 6));
      const month = parseInt(file.name.substring(7, 9));
      const year = parseInt(file.name.substring(10, 14));

      if ((year % 4 === 0 && year % 100 !== 0) || year % 400 === 0) {
        if (month === 2 && day !== 29) {
          showToast("error", "File name is invalid. dd should be last day of month.")
          setSelectedFiles([]);
          setFormData({
            file: null,
            name: ""
          });
          setIsButtonDisable(true);
          setIsLoading(false);
          return;
        }
      } else {
        if (month === 2 && day !== 28) {
          showToast("error", "File name is invalid. dd should be last day of month.")
          setSelectedFiles([]);
          setFormData({
            file: null,
            name: ""
          });
          setIsButtonDisable(true);
          setIsLoading(false);
          return;
        }
      }
      if (
        month !== 2 &&
        !(
          (month === 1 && day === 31) ||
          (month === 3 && day === 31) ||
          (month === 4 && day === 30) ||
          (month === 5 && day === 31) ||
          (month === 6 && day === 30) ||
          (month === 7 && day === 31) ||
          (month === 8 && day === 31) ||
          (month === 9 && day === 30) ||
          (month === 10 && day === 31) ||
          (month === 11 && day === 30) ||
          (month === 12 && day === 31)
        )
      ) {
        showToast("error", "File name is invalid. dd should be last day of month.")
        setSelectedFiles([]);
        setFormData({
          file: null,
          name: ""
        });
        setIsButtonDisable(true);
        setIsLoading(false);
        return;
      }
    }
    setIsButtonDisable(false);
    setIsLoading(false);
    setFileTypeError(false);
  }

  const showToast = (type, message) => {
    toast[type](message, {
      position: toast.POSITION.TOP_RIGHT,
      autoClose: 5000,
    });
  };

  const handleFileUpLoadInputChange = useCallback(
    (files) => {      
      const fileSizeLimit = 20971520;

      const allowedFileTypes = [
        // "application/pdf",
        "text/csv",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel", //added for ROE
      ];
      
      if (files.length > 0) {
        const validFiles = [];
        Object.values(files).forEach((file, fileIndex) => {
          const { type, size, name } = file;
          if(selectedFiles.some(currentFile => currentFile.name === name)){
            showToast("error", name + " Duplicate file");
            return;            
          }          
          
          if (!allowedFileTypes.includes(type)) {
            showToast("error", name + "Unsupported File Format");
          }

          if (size / fileSizeLimit > 1) {
            showToast("error", "File size should be less than or equal to 10 MB.");
            setFileTypeError(true);
          }
          if (name.length > 256) {
            showToast("error", "File name should be less than 256 characters.");
          }
          allowedFileTypes.includes(type) &&
            size / fileSizeLimit <= 1 &&
            name.length <= 256 &&
            validFiles.push(file);
        });
        const selectedFilesList = [...selectedFiles, ...validFiles];
        setSelectedFiles(selectedFilesList);

        setIsButtonDisable(selectedFilesList.length === 0);
        setIsLoading(selectedFilesList.length === 0);
        setFileTypeError(false);

        setFormData((data) => ({
          ...data,
          file: selectedFilesList,
        }));
      }
    },
    [selectedFiles]
  );

  const onDrop = useCallback((acceptedFiles) => {
    console.log("acceptedFiles : ", acceptedFiles)
    if (acceptedFiles && acceptedFiles.length > 0) {
      handleFileUpLoadInputChange(acceptedFiles);
    }
  }, [handleFileUpLoadInputChange]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
      'text/csv': ['.csv'],
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
    },
    multiple: isMultiple,
    disabled: isButtonDisable && !statementFieldsPrams?.statement_type && !fileTypeError,
    noClick: false,
    noKeyboard: false
  });

  const handleRemoveButton = (index) => {    
    setSelectedFiles(selectedFiles.filter((fileValue, fileIndex) => fileIndex !== index))
    setFormData({
      ...formData,
      file: formData.file.filter((fileValue, fileIndex) => fileIndex !== index)
    })
    console.log("Index : ", index, formData)
  }

  const handleCancelButton = () => {
    setFormData({
      file: null,
    });
    setStatementFieldsPrams({
      statement_type: "",
    });
    setUploadedFileNames([]);
    setSelectedFiles([]);
    inputRef.current.files = [];
    setIsButtonDisable(true);
    setIsLoading(true);
    setFileTypeError(false);
  };

  const handleStatementTypeDropdown = (name, value, reason) => {
    if (reason === "clear") {
      setFormData({
        file: null,
      });
      setStatementFieldsPrams({
        statement_type: "",
      });
      setUploadedFileNames([]);
      setSelectedFiles([]);
      inputRef.current.files = [];
    }
    setStatementFieldsPrams({
      ...statementFieldsPrams,
      [name]: value ?? "",
    });
  };

  return (
    <StyledContainer $toggle={toggle} sx={{ position: "relative" }}>
      <FloatingAIAssistant/>
      <TitleContainer>
        <UploadFileIcon className="title-icon" />
        <Typography variant="h5" className="page-heading">Statement Upload</Typography>
      </TitleContainer>

      <FormContainer elevation={0}>
        <Grid container spacing={3} sx={{ width: "100%", maxWidth: "100%", margin: 0 }}>
          <Grid item xs={12} sx={{ width: "100%", maxWidth: "100%", padding: "0 0 16px 0 !important" }}>
            <LabelContainer>
              <FolderIcon className="label-icon" />
              <StyledFormLabel>Statement Type</StyledFormLabel>
            </LabelContainer>
          <CommonSelect
            placeholder="Select Statement Type"
            value={statementFieldsPrams?.statement_type}
            handleChange={(event, value, reason) =>
              handleStatementTypeDropdown(
                "statement_type",
                value,
                reason,
                event
              )
            }
            options={statementTypes?.map((option) => option?.label) || []}
              customStyles={{ 
                width: "100%", 
                maxWidth: "100%",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  backgroundColor: "#f8f9fa",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: "#f0f4f8",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#1672DE",
                    },
                  },
                  "&.Mui-focused": {
                    backgroundColor: "#ffffff",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#1672DE",
                      borderWidth: "2px",
                    },
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#b0bec5",
                    transition: "all 0.3s ease",
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "#546e7a",
                  "&.Mui-focused": {
                    color: "#1672DE",
                  },
                },
                "& .MuiAutocomplete-input": {
                  color: "#263238",
                  fontSize: "14px",
                  fontWeight: "500",
                },
                "& .MuiAutocomplete-tag": {
                  backgroundColor: "#e3f2fd",
                  color: "#1672DE",
                  borderRadius: "6px",
                  fontWeight: "500",
                  "& .MuiChip-deleteIcon": {
                    color: "#1672DE",
                    "&:hover": {
                      color: "#1565c0",
                    },
                  },
                },
                "& .MuiAutocomplete-popupIndicator": {
                  color: "#546e7a",
                  "&:hover": {
                    color: "#1672DE",
                    backgroundColor: "rgba(22, 114, 222, 0.08)",
                  },
                },
                "& .MuiAutocomplete-clearIndicator": {
                  color: "#546e7a",
                  "&:hover": {
                    color: "#1672DE",
                    backgroundColor: "rgba(22, 114, 222, 0.08)",
                  },
                },
                "& .MuiAutocomplete-listbox": {
                  padding: "8px",
                  "& .MuiAutocomplete-option": {
                    borderRadius: "8px",
                    margin: "4px 0",
                    padding: "10px 14px",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: "rgba(22, 114, 222, 0.1)",
                    },
                    "&[aria-selected='true']": {
                      backgroundColor: "rgba(22, 114, 222, 0.15)",
                      fontWeight: "600",
                      "&.Mui-focused": {
                        backgroundColor: "rgba(22, 114, 222, 0.2)",
                      },
                    },
                    "&.Mui-focused": {
                      backgroundColor: "rgba(22, 114, 222, 0.1)",
                    },
                  },
                },
                "& .MuiPaper-root": {
                  borderRadius: "12px",
                  boxShadow: "0 8px 24px rgba(22, 114, 222, 0.15)",
                  border: "1px solid rgba(22, 114, 222, 0.1)",
                  marginTop: "8px",
                },
              }}
          />
        </Grid>

          <Grid item xs={12} sx={{ width: "100%", maxWidth: "100%", padding: "0 !important" }}>
            <LabelContainer>
              <DescriptionIcon className="label-icon" />
              <StyledFormLabel>Select File</StyledFormLabel>
              {statementFieldsPrams?.statement_type && (
                <Tooltip
                  title={
                    <Box>
                      {statementFieldsPrams?.statement_type === "ROE" ? (
                        <Typography fontSize={12} color={"#FFFFFF"} component="div">
                          <strong>Instructions for ROE file upload:</strong>
                <br />
                          {"1. The file name should be in the format: roe-<dd-mm-yyyy>.xlsx"}
                <br />
                          {"2. <dd> should be last day of the month"}
                <br />
                          {"3. The column names must be 'month', 'Premium Currency', and 'ROE', and must be present in the same order in the file"}
              </Typography>
                      ) : statementFieldsPrams?.statement_type === "Bank Statement" ? (
                        <Typography fontSize={12} color={"#FFFFFF"} component="div">
                          <strong>Instructions for Bank Statement file upload:</strong>
                          <br />
                          {"1. Multiple files are allowed"}
                          <br />
                          {"2. File name format: bank_statement_<dd-mm-yyyy>.<extension>"}
                          <br />
                          {"3. Supported formats: .xlsx, .csv, .pdf"}
                        </Typography>
                      ) : statementFieldsPrams?.statement_type === "Remittance" ? (
                        <Typography fontSize={12} color={"#FFFFFF"} component="div">
                          <strong>Instructions for Remittance file upload:</strong>
                          <br />
                          {"1. Only single file is allowed"}
                          <br />
                          {"2. File must contain required columns as per policy file format"}
                          <br />
                          {"3. Supported formats: .xlsx, .csv"}
                        </Typography>
                      ) : (
                        <Typography fontSize={12} color={"#FFFFFF"} component="div">
                          <strong>{`Instructions for ${statementFieldsPrams?.statement_type} file upload:`}</strong>
                          <br />
                          {`1. File name format: ${statementFieldsPrams?.statement_type.toLowerCase().replace(/\s+/g, '_')}_<dd-mm-yyyy>.<extension>`}
                          <br />
                          {"2. Date format: dd-mm-yyyy (e.g., 25-12-2024)"}
                          <br />
                          {"3. Supported formats: .xlsx, .csv, .pdf"}
                          <br />
                          {"4. File size should be less than 20 MB"}
                        </Typography>
                      )}
                    </Box>
                  }
                  arrow
                  placement="top"
                >
                  <InfoTooltip>
                    <InfoIcon sx={{ fontSize: "14px" }} />
                  </InfoTooltip>
            </Tooltip>
              )}
            </LabelContainer>
            <DropzoneContainer
            {...getRootProps()}
              isDisabled={isButtonDisable && !statementFieldsPrams?.statement_type && !fileTypeError}
              isDragActive={isDragActive}
              hasFiles={selectedFiles?.length > 0}
            title={isButtonDisable && !statementFieldsPrams?.statement_type && !fileTypeError ? "Please select statement type" : ""}
          >
            <input
              {...getInputProps({
                name: "file",
                accept: ".doc,.docx,.xlsx,.csv,.pdf",
              })}
              required
            />
              <DropzoneContent>
                {selectedFiles?.length > 0 ? (
                  <Box sx={{ width: "100%", maxHeight: "300px", overflowY: "auto", overflowX: "hidden" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, justifyContent: "center" }}>
                      <CheckCircleIcon sx={{ color: "#4caf50", fontSize: "20px" }} />
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: "#1672DE", 
                          fontWeight: "600",
                          fontSize: "14px"
                        }}
                      >
                        Selected Files ({selectedFiles.length})
              </Typography>
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                      {selectedFiles.map((file, index) => (
                        <Grow in={true} timeout={300} key={index}>
                          <Paper
                            elevation={0}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "12px",
                              padding: "10px 14px",
                              backgroundColor: "#ffffff",
                              borderRadius: "8px",
                              border: "1px solid rgba(22, 114, 222, 0.1)",
                              transition: "all 0.3s ease",
                              "&:hover": {
                                boxShadow: "0 2px 8px rgba(22, 114, 222, 0.15)",
                                transform: "translateX(2px)",
                              },
                            }}
                          >
                            <DescriptionIcon sx={{ color: "#1672DE", fontSize: "20px", flexShrink: 0 }} />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "#263238",
                                  fontWeight: "500",
                                  fontSize: "13px",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {file.name}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ color: "#90a4ae", fontSize: "11px" }}
                              >
                                {(file.size / 1024 / 1024).toFixed(2)} MB
                              </Typography>
                            </Box>
                            <DeleteIcon
                              sx={{
                                color: "#ef5350",
                                cursor: "pointer",
                                fontSize: "18px",
                                transition: "all 0.3s ease",
                                flexShrink: 0,
                                "&:hover": {
                                  color: "#d32f2f",
                                  transform: "scale(1.2)",
                                },
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveButton(index);
                              }}
                            />
                          </Paper>
                        </Grow>
                      ))}
                    </Box>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: "#90a4ae", 
                        fontSize: "11px",
                        mt: 2,
                        display: "block",
                        textAlign: "center"
                      }}
                    >
                      Click or drag to add more files
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <CloudUploadIcon 
                      sx={{ 
                        fontSize: "48px", 
                        color: isDragActive ? "#1672DE" : "#90a4ae",
                        transition: "all 0.3s ease",
                        animation: isDragActive ? `${pulse} 1s ease-in-out infinite` : "none"
                      }} 
                    />
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        color: "#546e7a", 
                        fontWeight: "500",
                        fontSize: "14px",
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                      }}
                    >
                      {isDragActive 
                        ? "Drop files here" 
                        : "Drag 'n' drop your file here, or click to select"}
                    </Typography>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: "#90a4ae", 
                        fontSize: "12px",
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                      }}
                    >
                      Supported formats: .xlsx, .csv, .pdf
                    </Typography>
                  </>
                )}
              </DropzoneContent>
            </DropzoneContainer>
        </Grid>

          <Grid item xs={12} sx={{ width: "100%", maxWidth: "100%", padding: "16px 0 0 0 !important", position: "relative" }}>
            <ActionButtonsContainer>
          <CommonButton
            text="Clear"
            handleClick={handleCancelButton}
                hoverColor="#1565c0"
            disabled={
              isButtonDisable &&
              !statementFieldsPrams?.statement_type &&
              !fileTypeError
            }
            style={{
              backgroundColor: "transparent",
              color:
                isButtonDisable &&
                  !statementFieldsPrams?.statement_type &&
                  !fileTypeError
                  ? "rgba(0, 0, 0, 0.26)"
                      : "#1672DE",
                  border: "1px solid #1672DE",
                  minWidth: "120px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
                icon={<ClearIcon sx={{ ml: 0.5 }} />}
              />
          <CommonButton
            text="Upload"
            handleClick={handleSubmitFileUpLoad}
                hoverColor="#1565c0"
            disabled={
              isButtonDisable ||
              !statementFieldsPrams?.statement_type ||
              fileTypeError ||
                  !selectedFiles.length ||
                  isLoading
                }
                style={{
                  backgroundColor: "#1672DE",
                  color: "white",
                  minWidth: "120px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
                icon={<CloudUploadIcon sx={{ ml: 0.5 }} />}
              />
            </ActionButtonsContainer>
            {isLoading && (
              <Box sx={{ 
                display: "flex", 
                alignItems: "center", 
                justifyContent: "center",
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                zIndex: 9999,
                backgroundColor: "rgba(255, 255, 255, 0.95)",
                padding: "20px",
                borderRadius: "8px",
                boxShadow: "0 4px 20px rgba(22, 114, 222, 0.2)",
                border: "1px solid rgba(22, 114, 222, 0.1)"
              }}>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={40} sx={{ color: "#1672DE" }} />
                  <Typography variant="body2" sx={{ color: "#1672DE", fontWeight: "500" }}>
                    Uploading...
                  </Typography>
                </Box>
              </Box>
            )}
        </Grid>
      </Grid>
      </FormContainer>


      {fileTypeError && (
        <Fade in={fileTypeError}>
          <Box
            sx={{
              mt: 2,
              p: 2,
              borderRadius: "8px",
              backgroundColor: "#ffebee",
              border: "1px solid #ef5350",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <ErrorIcon sx={{ color: "#d32f2f" }} />
            <Typography color={"#d32f2f"} sx={{ fontWeight: "500" }}>
              Unsupported File Format
            </Typography>
          </Box>
        </Fade>
      )}

          {uploadedFileNames?.length > 0 && (
        <FileListContainer>
          <LabelContainer sx={{ mt: 3, mb: 2 }}>
            <CheckCircleIcon className="label-icon" sx={{ color: "#4caf50" }} />
            <StyledFormLabel sx={{ color: "#1672DE", fontSize: "16px" }}>
              Uploaded Files
            </StyledFormLabel>
          </LabelContainer>
          <Box>
                {uploadedFileNames.map((fileName, index) => (
              <Grow in={true} timeout={300} key={index}>
                <FileItem elevation={0}>
                  <CheckCircleIcon sx={{ color: "#4caf50", fontSize: "24px" }} />
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#263238",
                      fontWeight: "500",
                      flex: 1,
                    }}
                  >
                    {fileName}
                  </Typography>
                </FileItem>
              </Grow>
            ))}
    </Box>
        </FileListContainer>
      )}
    </StyledContainer>
  );
};
